import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { NewSelect, NewInput } from "../..";
import { NewFieldStyles, SelectFieldOption_Values } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useDeviceInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useDeviceInfo";
import { Pen, Section } from "./_Modals/newSectionModalInterfaces";
import { TerminalTypesInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getTerminalTypesCall";

interface NewSectionModalLvl2_Props {
  section: Section;
  onSectionChange: (updatedSection: Section) => void;
}

const handleValueChange = (section: Section, penIndex: number, newValue: string, counterKey: keyof Pen, terminalConfig: Omit<TerminalTypesInfo, "input">, counterUpdater: (count: number) => void, onSectionChange: (section: Section) => void) => {
  const updatedPens = section.pens.map((pen, index) => {
    if (index === penIndex) {
      return {
        ...pen,
        [counterKey]: newValue ? Number(newValue) : null,
        [counterKey+"Config"]: terminalConfig 
      };
    }
    return pen;
  });
  onSectionChange({ ...section, pens: updatedPens });
  counterUpdater(updatedPens.filter(pen => pen[counterKey] !== null && pen[counterKey] !== undefined).length); // Update the count
};

const NewSectionModalLvl2: React.FC<NewSectionModalLvl2_Props> = ({ section, onSectionChange }) => {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const [pensWithFeederOutput, setPensWithFeederOutput] = useState<number | null>(null);
  const [pensWithWaterOutput, setPensWithWaterOutput] = useState<number | null>(null);
  const [pensWithActivatorInput, setPensWithActivatorInput] = useState<number | null>(null);

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const terminalTypesState = useAppSelector(({graphQlData}) => (graphQlData.domain.terminalTypes.entities))
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);;

  const { data: deviceInfoState, call: getDeviceInfo } = useDeviceInfo(connection);

  const terminalTypes = useMemo(() => {
    return terminalTypesState[domain]?.data ?? [];
  },[domain, terminalTypesState])

  const {__typename: inputTypename , input: inputBoolean, ...defaultInputTerminalConfig} = terminalTypes.find((terminalType) => (terminalType.terminal_type_id === 4))!;

  const {__typename, input, ...defaultOutputTerminalConfig} = terminalTypes.find((terminalTypes) => (terminalTypes.terminal_type_id === 50))!;

  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  },[deviceInfoState, domain])

  useEffect(() => {
    getDeviceInfo({fetchAmount: 500}, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) 
  
  useEffect(() => {
    if (section.pens) {
      setPensWithFeederOutput(section.pens.filter((pen) => pen.feederOutput != null).length)
      setPensWithWaterOutput(section.pens.filter((pen) => pen.waterOutput != null).length)
      setPensWithActivatorInput(section.pens.filter((pen) => pen.activatorInput != null).length)
    }
  },[section.pens, section])

  // Total number of pens
  const activePensCount = section.pens.length;
  const totalOutputs = section.water ? (activePensCount * 2) : activePensCount
  const totalInputs = section.activator ? activePensCount : 0
  const remainingOutputs = section.water ? (totalOutputs - ((pensWithFeederOutput ?? 0) + (pensWithWaterOutput ?? 0))) : (totalOutputs - (pensWithFeederOutput ?? 0)) 
  const remainingInputs = totalInputs - (pensWithActivatorInput ?? 0)

  // SelectField Options
  const ioOptions = deviceInfo.map((type) => ({
    label: type.name, 
    id: type.id
  }));

  // Handler for select changes in IO
  const handlePenIOChange = (penId: string, value?: SelectFieldOption_Values) => {
    const updatedPens = section.pens.map((pen) =>
      pen.penId === penId ? { ...pen, io: value?.id ? Number(value.id) : null } : pen
    );
    onSectionChange({ ...section, pens: updatedPens });
  };

  // #region | IO Fields
  // TODO selection of terminal types
  const ioFields = section.pens.filter((pen) => pen.penId !==null).map((pen, index) => {
      const ioFields = (
        <div style={{display: "flex", flexDirection: mobileXs ? "column" : "row", alignItems: mobileXs ? "flex-start" : "center", justifyContent: "start", gap: "9px"}}>
          <NewInput
            header={t("locationFeederPages.sections.feederOutput", {defaultValue: "Feeder Output"})}
            placeholder={"0"} 
            onChange={(newValue) => 
              handleValueChange(section, index, newValue, "feederOutput", defaultOutputTerminalConfig, setPensWithFeederOutput, onSectionChange)
            } 
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldWidth: mobileXs ? "100%" : "180px" 
            }}
            valueOverrides={{
              defaultValue: pen.feederOutput ?? undefined,
              valueType: "number",
              minValueNumber: 1
            }}
          />
          {section.water && (
            <NewInput
              placeholder={"0"} 
              header={t("locationFeederPages.sections.waterOutput", {defaultValue: "Water Output"})}
              onChange={(newValue) => 
                handleValueChange(section, index, newValue, "waterOutput", defaultOutputTerminalConfig, setPensWithWaterOutput, onSectionChange)
              } 
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldWidth: mobileXs ? "100%" : "180px" 
              }}
              valueOverrides={{
                defaultValue: pen.waterOutput ?? undefined,
                valueType: "number",
                minValueNumber: 1
              }}
            />
          )}
          {section.activator && (
            <NewInput
              header={t("locationFeederPages.sections.activatorInput", {defaultValue: "Activator Input"})}
              placeholder={"0"} 
              onChange={(newValue) => 
                handleValueChange(section, index, newValue, "activatorInput", defaultInputTerminalConfig, setPensWithActivatorInput, onSectionChange)
              } 
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary,
                fieldWidth: mobileXs ? "100%" : "180px" 
              }}
              valueOverrides={{
                defaultValue: pen.activatorInput ?? undefined,
                valueType: "number",
                minValueNumber: 1
              }}
            />
          )}
        </div>
      );
    
      return (
        <div key={pen.penId} style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
          <NewSelect
            key={"select_"+pen.penId}
            header={`${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${pen.name}`}
            placeholder={t("devices.chooseIo", {defaultValue: "Choose I/O"})}
            options={ioOptions}
            onChange={(selectedOption) => { if (pen.penId) { handlePenIOChange(pen.penId, selectedOption) } }}
            valueOverrides={{
              defaultValue: ioOptions.find(option => option.id === pen.io)
            }}
          />
          <div style={{
            width: "calc(100% - 20px)",
            height: "fit-content",
            backgroundColor: colors.greyLightSecondary,
            padding: "10px 10px",
            borderRadius: "10px"}}>
            {ioFields}  
          </div>
        </div>
      );
    })
  // #endregion

  return (
    <div style={{ width: "100%", borderTop: "2px solid " + colors.blueDarkSecondary, margin: "20px 0px" }}>
      <div style={{ padding: "10px 10px", maxHeight: "40vh", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary + colors.transparent, display: "flex", alignItems: "center", justifyContent: "start", flexDirection: "column", gap: "15px" }}>
        {ioFields}  
      </div>
      <div style={{borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, padding: "10px", maxHeight: "200px", display: "flex", flexDirection: mobileXs ? "column" : "row", gap: mobileXs ? "10px" : undefined, alignItems: "center", justifyContent: "space-around" }}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingOutputs", {defaultValue:"Remaining Outputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingOutputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingOutputs}</p>
        </div>
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"}}>         
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`${t("devices.remainingInputs", {defaultValue:"Remaining Inputs"})}:`}</p>
          <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary, backgroundColor: remainingInputs === 0 ? colors.systemGreen : colors.systemRed, padding: "4px 5px", borderRadius: "6px"}}>{remainingInputs}</p>
        </div>
      </div>
    </div>
  );
};

export default NewSectionModalLvl2;
